import {
  Box,
  Button,
  Divider,
  Heading,
  Link,
  Portal,
  Stack,
  Text,
  useTheme,
} from "@chakra-ui/react"
import { AnimatePresence, motion, Variants } from "framer-motion"
import React, { useCallback, useState } from "react"
import { Trans, useTranslation } from "react-i18next"
import CookieConsentPreferencesPanel from "./CookieConsentPreferencesPanel"
import { CookieCategory, CookieConsentPreferences } from "./CookieConsentTypes"

type View = "summary" | "customize"

interface Props {
  isOpen: boolean
  initialView?: View
  isPrivacySignalOn?: boolean
  defaultPreferences: CookieConsentPreferences
  privacyPolicyUrl: string
  onAcceptAll: () => void
  onRejectAll: () => void
  onSavePreferences: (preferences: CookieConsentPreferences) => void
}

const MotionBox = motion(Box)

const cardVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 24,
    transition: { duration: 0.2, ease: "easeIn" },
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.25, ease: "easeOut" },
  },
}

const CookieConsentBanner: React.FC<Props> = ({
  isOpen,
  initialView = "summary",
  isPrivacySignalOn = false,
  defaultPreferences,
  privacyPolicyUrl,
  onAcceptAll,
  onRejectAll,
  onSavePreferences,
}) => {
  const { t } = useTranslation()
  const {
    colors: { primary, gray },
  } = useTheme()

  const [view, setView] = useState<View>(initialView)
  const [draftPreferences, setDraftPreferences] =
    useState<CookieConsentPreferences>(defaultPreferences)

  const onToggle = useCallback(
    (category: CookieCategory, isEnabled: boolean) => {
      setDraftPreferences(current => ({ ...current, [category]: isEnabled }))
    },
    []
  )

  const onSave = useCallback(() => {
    onSavePreferences(draftPreferences)
  }, [draftPreferences, onSavePreferences])

  const isCustomizing = view === "customize"
  const isSummary = view === "summary"

  const outlineButtonStyle = {
    variant: "outline",
    _hover: { backgroundColor: "transparent" },
    _focus: { boxShadow: `${gray[400]} 0 0 0 3px` },
  }

  const primaryButtonStyle = {
    color: "primary.text",
    _focus: { boxShadow: `${primary[600]} 0 0 0 3px` },
  }

  // buttons share one row, so long labels wrap instead of overflowing; mobile
  // uses sm sizing
  const rowButtonStyle = {
    flex: { base: 1, md: "initial" },
    whiteSpace: "normal",
    textAlign: "center",
    height: "auto",
    minH: { base: 8, md: 10 },
    px: { base: 3, md: 4 },
    py: { base: 1.5, md: 2 },
    fontSize: { base: "sm", md: "md" },
  } as const

  const rejectAllButton = (
    <Button
      id="p-cookie-consent-reject-all-button"
      data-testid="p-cookie-consent-reject-all-button"
      colorScheme="primary"
      onClick={onRejectAll}
      {...rowButtonStyle}
      {...outlineButtonStyle}
    >
      {isPrivacySignalOn
        ? t("components.cookie-consent.CookieConsentBanner.button.keepOptedOut")
        : t("components.cookie-consent.CookieConsentBanner.button.rejectAll")}
    </Button>
  )

  const acceptAllButton = (
    <Button
      id="p-cookie-consent-accept-all-button"
      data-testid="p-cookie-consent-accept-all-button"
      colorScheme="primary"
      onClick={onAcceptAll}
      {...rowButtonStyle}
      // under the privacy signal, sharing is offered but not pushed
      {...(isPrivacySignalOn ? outlineButtonStyle : primaryButtonStyle)}
    >
      {isPrivacySignalOn
        ? t("components.cookie-consent.CookieConsentBanner.button.allowSharing")
        : t("components.cookie-consent.CookieConsentBanner.button.acceptAll")}
    </Button>
  )

  return (
    <AnimatePresence>
      {isOpen && (
        <Portal key="cookie-consent">
          <MotionBox
            id="p-cookie-consent"
            data-testid="p-cookie-consent"
            role="dialog"
            aria-labelledby="p-cookie-consent-title"
            aria-describedby="p-cookie-consent-description"
            variants={cardVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            position="fixed"
            bottom={0}
            left={0}
            right={{ base: 0, md: "auto" }}
            m={{ base: 0, md: 4 }}
            width={{ base: "auto", md: "full" }}
            maxW={{ md: "540px" }}
            maxH={{ base: "85vh", md: "calc(100vh - 2rem)" }}
            display="flex"
            flexDirection="column"
            bg="white"
            color="gray.800"
            boxShadow="xl"
            borderWidth={1}
            borderColor="gray.200"
            borderTopRadius={{ base: "3xl", md: "lg" }}
            borderBottomRadius={{ base: 0, md: "lg" }}
          >
            <Box px={5} pt={5} pb={2} flexShrink={0}>
              <Heading as="h2" id="p-cookie-consent-title" size="md">
                {t("components.cookie-consent.CookieConsentBanner.title")}
              </Heading>
            </Box>
            <Box px={5} py={2} flex="1" minH={0} overflowY="auto">
              <Text
                id="p-cookie-consent-description"
                fontSize={{ base: "xs", md: "sm" }}
              >
                <Trans
                  i18nKey={
                    isPrivacySignalOn
                      ? "components.cookie-consent.CookieConsentBanner.privacySignalNotice"
                      : "components.cookie-consent.CookieConsentBanner.description"
                  }
                  t={t}
                >
                  <Link
                    id="p-cookie-consent-privacy-policy-link"
                    data-testid="p-cookie-consent-privacy-policy-link"
                    href={privacyPolicyUrl || undefined}
                    isExternal={true}
                    textDecoration="underline"
                  />
                </Trans>
              </Text>
              {isCustomizing && (
                <>
                  <Divider my={4} />
                  <CookieConsentPreferencesPanel
                    preferences={draftPreferences}
                    onToggle={onToggle}
                  />
                </>
              )}
            </Box>
            <Box px={5} pt={2} pb={5} flexShrink={0}>
              <Stack
                direction="row"
                spacing={3}
                width="full"
                justifyContent="flex-end"
              >
                {isCustomizing && (
                  <>
                    <Button
                      id="p-cookie-consent-back-button"
                      data-testid="p-cookie-consent-back-button"
                      variant="ghost"
                      onClick={() => setView("summary")}
                      {...rowButtonStyle}
                    >
                      {t(
                        "components.cookie-consent.CookieConsentBanner.button.back"
                      )}
                    </Button>
                    <Button
                      id="p-cookie-consent-save-preferences-button"
                      data-testid="p-cookie-consent-save-preferences-button"
                      colorScheme="primary"
                      color="primary.text"
                      onClick={onSave}
                      {...rowButtonStyle}
                      _focus={{
                        boxShadow: `${primary[600]} 0 0 0 3px`,
                      }}
                    >
                      {t(
                        "components.cookie-consent.CookieConsentBanner.button.savePreferences"
                      )}
                    </Button>
                  </>
                )}
                {isSummary && (
                  <>
                    <Button
                      id="p-cookie-consent-customize-button"
                      data-testid="p-cookie-consent-customize-button"
                      colorScheme="primary"
                      variant="outline"
                      _hover={{ backgroundColor: "transparent" }}
                      onClick={() => setView("customize")}
                      {...rowButtonStyle}
                    >
                      {t(
                        "components.cookie-consent.CookieConsentBanner.button.customize"
                      )}
                    </Button>
                    {/* the privacy signal puts the primary choice in the middle */}
                    {isPrivacySignalOn ? acceptAllButton : rejectAllButton}
                    {isPrivacySignalOn ? rejectAllButton : acceptAllButton}
                  </>
                )}
              </Stack>
            </Box>
          </MotionBox>
        </Portal>
      )}
    </AnimatePresence>
  )
}

export default CookieConsentBanner
